// Ét fokuslag ad gangen (Spor: kortet). Ringmenuen, hændelseskortet, bykortet, pingehjulet og
// gavevalget er alle noget, spilleren skal se på og svare på. Står to fremme, dækker de hinanden, og
// hintet eller en toast lagde sig under dem. Reglerne:
//
//  - Gavevalget (årsskiftets skærm) går foran alt.
//  - Det nyeste vinder. Åbner spilleren en ny menu, lukkes den gamle.
//  - IGN-løkke 2, runde 3: et hændelseskort, der kommer, mens ringen eller bykortet står, lukker dem og
//    står fremme. Før ventede det usynligt bag bykortet, mens uret stod stille, og spilleren så kun en
//    grå værktøjslinje ("Kortet først") i op til 90 s. Åbner spilleren selv en menu, mens kortet venter,
//    vinder menuen, men kortet står tilbage som en lille fane ("venter"), der aldrig er usynlig.
//  - Pingehjulet lukkes ikke af et kort: makkeren er midt i at sige noget, og i rummet går uret.
//  - Mens et fokuslag står, venter toasts i køen, og hintet, hånden og Volts boble er væk.

export type FocusLayer = 'gave' | 'ping' | 'ring' | 'bykort' | 'kort';

/** Lag, spilleren selv åbner og lukker. De udelukker hinanden: det nyeste vinder. */
const OWN: readonly FocusLayer[] = ['ping', 'ring', 'bykort'];

/** Lag, et nyt hændelseskort lukker, når de stod før kortet. */
const CARD_CLOSES: readonly FocusLayer[] = ['ring', 'bykort'];

export interface Focus {
  /** Laget, der står fremme (null, når intet vil frem). */
  top: FocusLayer | null;
  /** Lag, der vil frem, men venter (kortet bag en menu, som en synlig fane). */
  waiting: FocusLayer[];
  /** Spillerens egne lag, der skal lukkes, fordi et nyere er åbnet. */
  close: FocusLayer[];
}

export class FocusStack {
  /** Hvornår hvert lag sidst blev åbnet (rækkefølge, ikke tid). */
  private opened = new Map<FocusLayer, number>();
  private seq = 0;
  private cur: Focus = { top: null, waiting: [], close: [] };

  /** Hvad vil frem i denne frame? Svarer med det, der får lov. */
  update(want: Partial<Record<FocusLayer, boolean>>): Focus {
    for (const l of [...OWN, 'kort'] as FocusLayer[]) {
      if (!want[l]) this.opened.delete(l);
      else if (!this.opened.has(l)) this.opened.set(l, ++this.seq);
    }
    const at = (l: FocusLayer): number => this.opened.get(l) ?? 0;
    const kortAt = want.kort ? at('kort') : 0;
    // Ring og bykort, der stod, før kortet kom, lukkes: kortet vinder over det, der var der i forvejen.
    const stale = OWN.filter((l) => want[l] && CARD_CLOSES.includes(l) && kortAt > at(l));
    const own = OWN.filter((l) => want[l] && !stale.includes(l)).sort((a, b) => at(b) - at(a));
    const newest = own[0] ?? null;
    const close = [...own.slice(1), ...stale];
    let top: FocusLayer | null;
    if (want.gave) top = 'gave';
    else if (newest) top = newest;
    else top = want.kort ? 'kort' : null;
    const waiting: FocusLayer[] = [];
    if (want.kort && top !== 'kort') waiting.push('kort');
    if (want.gave && newest) waiting.push(newest);
    this.cur = { top, waiting, close };
    return this.cur;
  }

  get top(): FocusLayer | null {
    return this.cur.top;
  }

  /** Venter kortet bag spillerens egen menu (ikke bag en skærm)? Så står det som en fane. */
  get peeking(): boolean {
    return this.cur.waiting.includes('kort') && this.cur.top !== 'gave' && this.cur.top !== null;
  }

  /** Står et lag på selve banen (ikke en skærm)? Så er hint, toast, hånd og Volts boble væk. */
  get quiet(): boolean {
    return this.cur.top !== null && this.cur.top !== 'gave';
  }
}
